/**
 * Bot detail page (ported from CC `AgentDetail.tsx` onto TanStack
 * Router, plus the new V3.1 tabs):
 *
 *   - PnL chart (24h cumulative), command history
 *   - rename / pause / resume / restart / push-config actions
 *   - "Actions"      → GET /api/v1/fleet/agents/:id/actions (paginated journal)
 *   - "Assignments"  → GET /api/v1/fleet/agents/:id/assignments with
 *                      per-market pause/resume (POST /api/v1/fleet/assignments/…)
 */
import { useState } from "react";
import {
  ActionIcon, Alert, Badge, Button, Code, Grid, Group, Menu, Modal, Paper,
  Stack, Table, Tabs, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft, IconChevronDown, IconPencil, IconPlayerPause, IconPlayerPlay,
  IconPower, IconRefresh, IconSettings, IconTrash,
} from "@tabler/icons-react";
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { flattenActionPages, fleetApi, type Assignment } from "../../api/fleet";
import { useIssueCommand } from "../../components/fleet/AgentCard";
import { PnlChart } from "../../components/fleet/PnlChart";
import { StatusBadge } from "../../components/fleet/StatusBadge";
import { useSSE } from "../../hooks/useSSE";
import { ApplyConfigModal } from "./FleetAssignments";

export function FleetBotDetailPage() {
  const params = useParams({ strict: false }) as { botId?: string };
  const id = params.botId ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const botQ = useQuery({
    queryKey: ["fleet", "agent", id],
    queryFn: () => fleetApi.agents.get(id),
    enabled: Boolean(id),
  });
  const bot = botQ.data;

  // Last 24h, sampled every ~10s ⇒ up to ~8640 rows; bucketed to 5 min
  // by the chart for ~288 plotted points.
  const statsQ = useQuery({
    queryKey: ["fleet", "agent", id, "stats"],
    queryFn: () =>
      fleetApi.agents.stats(id, {
        limit: 10_000,
        since: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      }),
    enabled: Boolean(id),
    refetchInterval: 10_000,
  });
  const cmdsQ = useQuery({
    queryKey: ["fleet", "agent", id, "commands"],
    queryFn: () => fleetApi.agents.commands(id),
    enabled: Boolean(id),
    refetchInterval: 5_000,
  });

  const issueCmd = useIssueCommand();
  const updateBot = useMutation({
    mutationFn: (body: { display_name: string }) => fleetApi.agents.update(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fleet", "agent", id] });
      void qc.invalidateQueries({ queryKey: ["fleet", "agents"] });
    },
  });
  const removeBot = useMutation({
    mutationFn: () => fleetApi.agents.disable(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["fleet", "agents"] }),
  });

  const [configModal, setConfigModal] = useState(false);
  const [danger, setDanger] = useState<"reboot" | "delete" | null>(null);
  const [renameModal, setRenameModal] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");

  useSSE({
    "bot.stats": (d) => { if (d.agent_id === id) void botQ.refetch(); },
    "bot.status": (d) => { if (d.agent_id === id) void botQ.refetch(); },
  });

  if (!bot) return <Text c="dimmed">Loading…</Text>;

  const sendCmd = async (verb: string, payload: Record<string, unknown> = {}) => {
    try {
      await issueCmd.mutateAsync({ agent_id: id, verb, payload });
      notifications.show({ color: "green", message: `Command "${verb}" sent.` });
    } catch {
      notifications.show({ color: "red", message: "Failed to send command." });
    }
  };

  const chartData = [...(statsQ.data ?? [])].reverse().map((s) => ({
    ts: s.ts,
    pnl: parseFloat(s.cumulative_pnl_usdc),
  }));

  return (
    <>
      <Group mb="lg" justify="space-between">
        <Group>
          <ActionIcon variant="subtle" onClick={() => void navigate({ to: "/fleet" })}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>{bot.display_name}</Title>
          <Tooltip label="Rename bot">
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Rename bot"
              onClick={() => { setRenameDraft(bot.display_name); setRenameModal(true); }}
            >
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <StatusBadge status={bot.status} />
        </Group>
        <Group gap="xs" wrap="wrap">
          <Button size="sm" variant="light" color="yellow" leftSection={<IconPlayerPause size={16} />}
            onClick={() => void sendCmd("pause")} disabled={bot.status !== "online"}>
            Pause
          </Button>
          <Button size="sm" variant="light" color="green" leftSection={<IconPlayerPlay size={16} />}
            onClick={() => void sendCmd("resume")} disabled={bot.status !== "paused"}>
            Resume
          </Button>
          <Button size="sm" leftSection={<IconSettings size={16} />} onClick={() => setConfigModal(true)}>
            Apply config
          </Button>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button size="sm" variant="default" rightSection={<IconChevronDown size={14} />}>More</Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={14} />}
                onClick={() => { setRenameDraft(bot.display_name); setRenameModal(true); }}>
                Rename
              </Menu.Item>
              <Menu.Item leftSection={<IconRefresh size={14} />} onClick={() => void sendCmd("restart")}>
                Restart bot software
              </Menu.Item>
              <Menu.Item leftSection={<IconPower size={14} />} color="orange" onClick={() => setDanger("reboot")}>
                Reboot Raspberry Pi
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => setDanger("delete")}>
                Delete bot
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      <Grid mb="md">
        {[
          { label: "Bot ID", value: bot.id },
          { label: "Active Config", value: bot.current_config_id ?? "—" },
          { label: "Last Seen", value: bot.last_seen_at ? new Date(bot.last_seen_at).toLocaleString() : "—" },
          { label: "Enrolled", value: new Date(bot.enrolled_at).toLocaleDateString() },
        ].map((item) => (
          <Grid.Col key={item.label} span={{ base: 12, sm: 6, md: 3 }}>
            <Paper p="sm" withBorder>
              <Text size="xs" c="dimmed">{item.label}</Text>
              <Text fw={500} size="sm" truncate>{item.value}</Text>
            </Paper>
          </Grid.Col>
        ))}
      </Grid>

      <Tabs defaultValue="chart">
        <Tabs.List mb="md">
          <Tabs.Tab value="chart">PnL Chart</Tabs.Tab>
          <Tabs.Tab value="commands">Commands</Tabs.Tab>
          <Tabs.Tab value="actions">Actions</Tabs.Tab>
          <Tabs.Tab value="assignments">Markets &amp; configs</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="chart">
          <Paper p="md" withBorder>
            <PnlChart data={chartData} />
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="commands">
          <Stack gap="xs">
            {cmdsQ.data?.length === 0 && <Text c="dimmed">No commands.</Text>}
            {cmdsQ.data?.map((cmd) => (
              <Paper key={cmd.id} p="sm" withBorder>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>{cmd.verb}</Text>
                  <Badge color={cmd.status === "acked" ? "green" : cmd.status === "failed" ? "red" : "gray"}>
                    {cmd.status}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">{new Date(cmd.created_at).toLocaleString()}</Text>
                {cmd.ack_error && <Text size="xs" c="red">{cmd.ack_error}</Text>}
              </Paper>
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="actions">
          <ActionsJournal agentId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="assignments">
          <AgentAssignments agentId={id} />
        </Tabs.Panel>
      </Tabs>

      {/* Rename Bot Modal */}
      <Modal opened={renameModal} onClose={() => setRenameModal(false)} title="Rename bot">
        <Stack>
          <TextInput
            label="Display name"
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.currentTarget.value)}
            placeholder="e.g. trader-east-1"
            maxLength={64}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRenameModal(false)}>Cancel</Button>
            <Button
              disabled={
                renameDraft.trim().length === 0 ||
                renameDraft.trim() === bot.display_name ||
                updateBot.isPending
              }
              loading={updateBot.isPending}
              onClick={async () => {
                try {
                  await updateBot.mutateAsync({ display_name: renameDraft.trim() });
                  notifications.show({ color: "green", message: "Bot renamed." });
                  setRenameModal(false);
                } catch {
                  notifications.show({ color: "red", message: "Failed to rename bot." });
                }
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Apply a trading config (per market) to this bot */}
      <ApplyConfigModal opened={configModal} onClose={() => setConfigModal(false)} initialAgentIds={[id]} />

      {/* Confirm reboot / delete */}
      <Modal
        opened={danger != null}
        onClose={() => setDanger(null)}
        title={danger === "delete" ? `Delete ${bot.display_name}?` : `Reboot ${bot.display_name}'s Raspberry Pi?`}
      >
        <Stack>
          <Text size="sm">
            {danger === "delete"
              ? "The bot is removed from the farm and can no longer connect. Its history stays in reports. To use this Pi again, enroll it as a new bot."
              : "The whole Pi restarts. The bot is offline for about a minute and comes back on its own."}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDanger(null)}>Cancel</Button>
            <Button
              color="red"
              loading={removeBot.isPending}
              onClick={async () => {
                if (danger === "delete") {
                  try {
                    await removeBot.mutateAsync();
                    notifications.show({ color: "green", message: "Bot deleted." });
                    void navigate({ to: "/fleet" });
                  } catch {
                    notifications.show({ color: "red", message: "Failed to delete bot." });
                  }
                } else {
                  await sendCmd("reboot");
                }
                setDanger(null);
              }}
            >
              {danger === "delete" ? "Delete bot" : "Reboot Pi"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/** Paginated `agent_actions` journal (V3.1 §4). */
function ActionsJournal({ agentId }: { agentId: string }) {
  const q = useInfiniteQuery({
    queryKey: ["fleet", "agent", agentId, "actions"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fleetApi.fleet.agentActions(agentId, { limit: 50, cursor: pageParam }),
    getNextPageParam: (last) =>
      last.has_more === false ? undefined : last.next_cursor ?? undefined,
    retry: false,
  });

  if (q.isError) {
    return (
      <Alert color="yellow">
        Action journal unavailable — the CC server does not expose
        <Code>/api/v1/fleet/agents/:id/actions</Code> yet.
      </Alert>
    );
  }
  const items = flattenActionPages(q.data?.pages);

  return (
    <Stack gap="xs">
      {q.isLoading && <Text c="dimmed">Loading…</Text>}
      {!q.isLoading && items.length === 0 && <Text c="dimmed">No actions journaled yet.</Text>}
      {items.map((a) => (
        <Paper key={String(a.id)} p="xs" withBorder>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
              <Badge size="xs" variant="light" color={kindColor(a.kind)}>{a.kind}</Badge>
              {a.market_id && <Badge size="xs" variant="outline">{a.market_id}</Badge>}
              {a.ref_id && <Text size="xs" c="dimmed" truncate>ref: {a.ref_id}</Text>}
            </Group>
            <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {new Date(a.ts).toLocaleString()}
            </Text>
          </Group>
          {a.payload != null && Object.keys(a.payload).length > 0 && (
            <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
              {JSON.stringify(a.payload)}
            </Text>
          )}
        </Paper>
      ))}
      {q.hasNextPage && (
        <Button
          variant="light"
          onClick={() => void q.fetchNextPage()}
          loading={q.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </Stack>
  );
}

function kindColor(kind: string): string {
  switch (kind) {
    case "signal_sent": return "blue";
    case "signal_acked": return "green";
    case "order_event": return "cyan";
    case "pause": return "yellow";
    case "resume": return "green";
    case "config_applied": return "violet";
    case "enroll": return "teal";
    case "health_alert": return "red";
    default: return "gray";
  }
}

/** Per-market assignments for one agent, with pause/resume buttons. */
function AgentAssignments({ agentId }: { agentId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["fleet", "assignments", "agent", agentId],
    queryFn: () => fleetApi.fleet.agentAssignments(agentId),
    retry: false,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["fleet", "assignments"] });
    void qc.invalidateQueries({ queryKey: ["fleet", "agent", agentId, "assignments"] });
  };
  const pause = useMutation({
    mutationFn: (marketId: string) =>
      fleetApi.fleet.pauseAssignments({ market_id: marketId, agent_ids: [agentId] }),
    onSuccess: invalidate,
  });
  const resume = useMutation({
    mutationFn: (marketId: string) =>
      fleetApi.fleet.resumeAssignments({ market_id: marketId, agent_ids: [agentId] }),
    onSuccess: invalidate,
  });

  if (q.isError) {
    return (
      <Alert color="yellow">
        Assignments unavailable — the CC server does not expose
        <Code>/api/v1/fleet/agents/:id/assignments</Code> yet.
      </Alert>
    );
  }
  const rows = q.data ?? [];

  return (
    <Stack gap="xs">
      {q.isLoading && <Text c="dimmed">Loading…</Text>}
      {!q.isLoading && rows.length === 0 && (
        <Text c="dimmed">No market assignments for this bot.</Text>
      )}
      {rows.length > 0 && (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Market</Table.Th>
              <Table.Th>Config version</Table.Th>
              <Table.Th>Mode</Table.Th>
              <Table.Th>State</Table.Th>
              <Table.Th>Activated</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((a: Assignment) => (
              <Table.Tr key={`${a.agent_id}:${a.market_id}`}>
                <Table.Td><Badge variant="outline">{a.market_id}</Badge></Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace" truncate maw={220}>
                    {a.active_config_version_id ?? "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={a.mode === "live" ? "red" : "blue"} variant="light">
                    {a.mode}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={a.paused ? "yellow" : "green"} variant="light">
                    {a.paused ? "paused" : "active"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {a.activated_at ? new Date(a.activated_at).toLocaleString() : "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} justify="flex-end" wrap="nowrap">
                    <Tooltip label="Pause this market on this bot">
                      <ActionIcon
                        size="sm" variant="light" color="yellow"
                        disabled={a.paused || pause.isPending}
                        onClick={() => pause.mutate(a.market_id)}
                      >
                        <IconPlayerPause size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Resume this market on this bot">
                      <ActionIcon
                        size="sm" variant="light" color="green"
                        disabled={!a.paused || resume.isPending}
                        onClick={() => resume.mutate(a.market_id)}
                      >
                        <IconPlayerPlay size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
